import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, MapPin, Star, Award, Tag, ChevronRight, Filter, X, ArrowLeft,
  Utensils, Home, Car, Heart, Sparkles, Briefcase, Building,
  Dumbbell, GraduationCap, PawPrint, Music, ShoppingBag, DollarSign,
  Laptop, Hammer, Scale, Stethoscope, Globe, Coffee, Plane, Loader2, UserPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { BookmarkButton } from '../components/social';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Directory Logo
const DIRECTORY_LOGO = "https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png";

const CATEGORY_ICONS = {
  'restaurants': Utensils, 'home-services': Home, 'automotive': Car,
  'health-wellness': Heart, 'beauty': Sparkles, 'professional-services': Briefcase,
  'real-estate': Building, 'fitness': Dumbbell, 'education': GraduationCap,
  'pet-services': PawPrint, 'entertainment': Music, 'retail': ShoppingBag,
  'financial': DollarSign, 'technology': Laptop, 'construction': Hammer,
  'legal': Scale, 'medical': Stethoscope, 'ecommerce': Globe,
  'food-beverage': Coffee, 'travel-hospitality': Plane,
};

function StarRating({ rating, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} className={`${sizeClass} ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} />
      ))}
    </div>
  );
}

function BusinessCard({ business }) {
  const Icon = CATEGORY_ICONS[business.category] || Globe;
  const token = localStorage.getItem('token');
  const [showPromoMenu, setShowPromoMenu] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(business.like_count || 0);
  const [promoLink, setPromoLink] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      window.location.href = '/register?plan=get-listed&action=like&business=' + business.id;
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/directory/businesses/${business.id}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (res.ok) {
        setLiked(data.liked);
        setLikeCount(data.like_count);
      }
    } catch (error) {
      console.error('Failed to like:', error);
    }
  };
  
  const handlePromote = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      window.location.href = '/register?plan=get-listed&action=promote&business=' + business.id;
      return;
    }
    
    if (showPromoMenu) {
      setShowPromoMenu(false);
      return;
    }
    
    // Generate promo link if not already done
    if (!promoLink) {
      try {
        const res = await fetch(`${API_URL}/api/directory/businesses/${business.id}/promote`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await res.json();
        if (res.ok) {
          setPromoLink(data.share_url);
        }
      } catch (error) {
        console.error('Failed to get promo link:', error);
      }
    }
    
    setShowPromoMenu(true);
  };
  
  const copyPromoLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (promoLink) {
      await navigator.clipboard.writeText(promoLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
      className="bg-[#0d1525] rounded-2xl p-5 border border-white/10 hover:border-cyan-500/30 transition-all h-full relative"
    >
      <Link to={`/directory/business/${business.id}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Icon className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-white line-clamp-1">{business.name}</h3>
              <p className="text-xs text-gray-400 capitalize">{business.subcategory}</p>
            </div>
          </div>
          {business.is_ai_certified && (
            <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 rounded-full">
              <Award className="w-3 h-3 text-amber-400" />
              <span className="text-[10px] text-amber-400 font-medium">Best</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 mb-3">
          <StarRating rating={Math.round(business.rating)} />
          <span className="text-sm font-medium text-white">{business.rating}</span>
          <span className="text-xs text-gray-500">({business.review_count})</span>
        </div>
        
        <p className="text-sm text-gray-400 line-clamp-2 mb-3">{business.description}</p>
        
        <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-white/5">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {business.city}
          </span>
          {token && (
            <div onClick={(e) => e.preventDefault()}>
              <BookmarkButton businessId={business.id} variant="card" />
            </div>
          )}
        </div>
      </Link>
      
      {/* Promote or Like Section */}
      <div className="mt-3 pt-3 border-t border-white/5">
        <div className="flex items-center justify-between">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all ${
              liked 
                ? 'bg-pink-500/20 text-pink-400' 
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
            }`}
            data-testid={`like-btn-${business.id}`}
          >
            <Heart className={`w-3.5 h-3.5 ${liked ? 'fill-pink-400' : ''}`} />
            {liked ? 'Liked!' : 'Like'} {likeCount > 0 && `(${likeCount})`}
          </button>
          
          <button
            onClick={handlePromote}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-gradient-to-r from-cyan-500/20 to-teal-500/20 text-cyan-400 hover:from-cyan-500/30 hover:to-teal-500/30 transition-all"
            data-testid={`promote-btn-${business.id}`}
          >
            <DollarSign className="w-3.5 h-3.5" />
            Promote & Earn
          </button>
        </div>
        
        {/* Promo Menu Dropdown */}
        {showPromoMenu && (
          <div className="absolute left-0 right-0 bottom-0 transform translate-y-full mt-2 p-3 bg-[#1a2235] rounded-xl border border-cyan-500/30 shadow-xl z-10">
            <p className="text-xs text-gray-400 mb-2">Share & earn from this business:</p>
            
            {promoLink && (
              <div className="mb-3 p-2 bg-black/30 rounded-lg">
                <p className="text-[10px] text-gray-500 mb-1">Your affiliate link:</p>
                <div className="flex items-center gap-2">
                  <input 
                    type="text" 
                    value={promoLink} 
                    readOnly 
                    className="flex-1 bg-transparent text-xs text-cyan-400 outline-none truncate"
                  />
                  <button 
                    onClick={copyPromoLink}
                    className="p-1.5 bg-cyan-500/20 rounded text-cyan-400 hover:bg-cyan-500/30"
                  >
                    {copySuccess ? '✓' : '📋'}
                  </button>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Link 
                to={token ? `/affiliate/promote/${business.id}` : `/register?plan=get-listed&action=affiliate`}
                className="block w-full text-left px-3 py-2 rounded-lg bg-cyan-500/10 text-cyan-400 text-xs hover:bg-cyan-500/20 transition-colors"
              >
                💰 View Affiliate Dashboard
              </Link>
              <Link 
                to={token ? `/publishing?promote=${business.id}` : `/register?plan=get-listed&action=publish`}
                className="block w-full text-left px-3 py-2 rounded-lg bg-purple-500/10 text-purple-400 text-xs hover:bg-purple-500/20 transition-colors"
              >
                📢 Publish & Share (uses credits)
              </Link>
            </div>
            <p className="text-[10px] text-gray-500 mt-2 text-center">
              {token ? 'Help your community & earn!' : 'Create account to start earning'}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function DirectoryCategoryPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiResponse, setAiResponse] = useState(null);
  const [externalLinks, setExternalLinks] = useState(null);
  const [recommendedActions, setRecommendedActions] = useState(null);
  const [filters, setFilters] = useState({
    search: searchParams.get('q') || '',
    aiCertified: searchParams.get('ai_certified') === 'true',
    minRating: searchParams.get('rating') || '',
    city: searchParams.get('city') || ''
  });

  const categoryInfo = categories.find(c => c.slug === slug);
  const Icon = CATEGORY_ICONS[slug] || Globe;

  // Store search query in sessionStorage when search is performed
  useEffect(() => {
    if (filters.search) {
      sessionStorage.setItem('lastSearchQuery', filters.search);
      sessionStorage.setItem('lastSearchUrl', window.location.href);
    }
  }, [filters.search]);

  useEffect(() => {
    loadData();
  }, [slug, filters]);

  const loadData = async () => {
    setLoading(true);
    setAiResponse(null);
    setExternalLinks(null);
    setRecommendedActions(null);
    try {
      // Load categories
      const catsRes = await fetch(`${API_URL}/api/directory/categories`);
      setCategories(await catsRes.json());

      // Use AI-powered search endpoint
      const searchData = {
        query: filters.search || '',
        category: slug || null,
        city: filters.city || null,
        ai_certified_only: filters.aiCertified,
        min_rating: filters.minRating ? parseFloat(filters.minRating) : null,
        limit: 50,
        skip: 0
      };

      const bizRes = await fetch(`${API_URL}/api/directory/ai-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchData)
      });
      
      const result = await bizRes.json();
      setBusinesses(result.businesses || []);
      
      // Set AI fallback response if no results
      if (result.ai_fallback) {
        setAiResponse(result.ai_fallback);
      }
      if (result.external_search_links) {
        setExternalLinks(result.external_search_links);
      }
      if (result.recommended_actions) {
        setRecommendedActions(result.recommended_actions);
      }
    } catch (error) {
      console.error('Failed to load:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white">
      {/* Nav - Dark Theme */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0d1525]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/directory" className="flex items-center gap-2">
                <img src={DIRECTORY_LOGO} alt="Directory" className="w-8 h-8 rounded-lg" />
                <span className="font-heading text-lg font-bold text-white hidden sm:block">Directory</span>
              </Link>
              <ChevronRight className="w-4 h-4 text-gray-500" />
              {filters.search ? (
                <span className="text-gray-400">Search: "{filters.search}"</span>
              ) : (
                <span className="text-gray-400">{categoryInfo?.name || 'Search Results'}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Link to="/directory/deals" className="text-gray-400 hover:text-white text-sm transition-colors">
                <Tag className="w-4 h-4 inline mr-1" />
                Deals
              </Link>
              <Link to="/directory/jobs" className="text-gray-400 hover:text-white text-sm transition-colors hidden sm:inline">
                Jobs
              </Link>
              <Link to="/login">
                <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">Enter</Button>
              </Link>
              <Link to="/pricing">
                <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                  <UserPlus className="w-4 h-4 mr-1" /> Free Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Back to Search Button - shows when coming from search */}
          {filters.search && (
            <div className="mb-4">
              <button 
                onClick={() => window.history.back()}
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Search Results
              </button>
            </div>
          )}
          
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Link to="/directory" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="w-14 h-14 rounded-2xl bg-cyan-500/20 flex items-center justify-center">
                <Icon className="w-7 h-7 text-cyan-400" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-white">
                  {categoryInfo?.name || 'All Businesses'}
                </h1>
                <p className="text-gray-400">{businesses.length} businesses found</p>
              </div>
            </div>

            {/* Filters - Dark Theme */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[200px] max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    placeholder="Search in this category..."
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setFilters({...filters, aiCertified: !filters.aiCertified})}
                className={filters.aiCertified ? 'bg-amber-500/20 border-amber-500/50 text-amber-400' : 'border-white/20 text-gray-400 hover:text-white'}
              >
                <Award className="w-4 h-4 mr-2" />
                AI Certified
              </Button>
              <select
                value={filters.minRating}
                onChange={(e) => setFilters({...filters, minRating: e.target.value})}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
              >
                <option value="">Any Rating</option>
                <option value="4">4+ Stars</option>
                <option value="4.5">4.5+ Stars</option>
              </select>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
            </div>
          ) : businesses.length === 0 ? (
            <div className="text-center py-12 bg-[#0d1525] rounded-2xl border border-white/10">
              <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No listings found for "{filters.search}"</h3>
              
              {/* AI Response */}
              {aiResponse && (
                <div className="max-w-2xl mx-auto mt-6 p-6 bg-gradient-to-br from-cyan-900/30 to-teal-900/30 rounded-xl border border-cyan-500/30 text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-cyan-400" />
                    </div>
                    <span className="text-sm font-medium text-cyan-400">AI Assistant</span>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{aiResponse}</p>
                </div>
              )}
              
              {/* Recommended Actions with Links */}
              {recommendedActions && (
                <div className="max-w-3xl mx-auto mt-8">
                  <p className="text-gray-400 text-sm mb-4">While you're here, check out these options:</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {recommendedActions.map((action, idx) => (
                      <Link key={idx} to={action.link}>
                        <div className={`p-4 rounded-xl border text-left transition-all hover:scale-[1.02] ${
                          action.type === 'gift' ? 'bg-purple-900/20 border-purple-500/30 hover:border-purple-500/50' :
                          action.type === 'coupon' ? 'bg-green-900/20 border-green-500/30 hover:border-green-500/50' :
                          action.type === 'listing' ? 'bg-amber-900/20 border-amber-500/30 hover:border-amber-500/50' :
                          'bg-cyan-900/20 border-cyan-500/30 hover:border-cyan-500/50'
                        }`}>
                          <h4 className="text-white font-medium text-sm mb-1">{action.title}</h4>
                          <p className="text-gray-400 text-xs">{action.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              
              {/* External Search Links */}
              {externalLinks && (
                <div className="mt-8">
                  <p className="text-gray-400 text-sm mb-4">Or try searching on:</p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <a 
                      href={externalLinks.gemini} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg text-blue-400 transition-colors"
                    >
                      <Sparkles className="w-4 h-4" />
                      Google Gemini
                    </a>
                    <a 
                      href={externalLinks.perplexity} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg text-purple-400 transition-colors"
                    >
                      <Globe className="w-4 h-4" />
                      Perplexity AI
                    </a>
                    <a 
                      href={externalLinks.duckduckgo} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600/20 hover:bg-orange-600/30 border border-orange-500/30 rounded-lg text-orange-400 transition-colors"
                    >
                      <Search className="w-4 h-4" />
                      DuckDuckGo
                    </a>
                  </div>
                </div>
              )}
              
              {/* Create Free Listing CTA */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-gray-400 text-sm mb-3">Are you a business owner?</p>
                <Link to="/directory/register?type=business">
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                    <Award className="w-4 h-4 mr-2" />
                    Create Your FREE Business Listing
                  </Button>
                </Link>
                <p className="text-gray-500 text-xs mt-2">Get listed free + 1 complimentary coupon</p>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.map((business) => (
                <BusinessCard key={business.id} business={business} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
